import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import type { CalendarEntry } from '~/api/types'
import { ListView } from '~/calendar/ListView'
import { MonthGrid } from '~/calendar/MonthGrid'
import { PhoneMonth } from '~/calendar/PhoneMonth'
import { PhoneBar } from '~/calendar/PhoneBar'
import { Toolbar } from '~/calendar/Toolbar'
import { WeekView } from '~/calendar/WeekView'
import { useCalendar } from '~/calendar/Data'
import { mondayOf, readAddress, weekLabel, weekSpansMonths, writeAddress } from '~/calendar/address'
import type { CalendarAddress } from '~/calendar/address'
import { MarksProvider } from '~/components/MarksContext'
import { act, type Settle } from '~/beacon'
import { opening } from '~/sheet/seed'
import { defaultView, inView, opens } from '~/calendar/views'

import s from './Calendar.module.css'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * 720px, asked rather than assumed: the phone is a different design, not a narrower one.
 *
 * Guarded on `matchMedia` itself, not only on `window`. Everything that renders this has one;
 * what does not is jsdom, and a view that throws in the test environment reads as broken
 * rather than as untested.
 */
function isPhone(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 720px)').matches
}


function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

/** The last night of a month, as a date. Day 0 of the next month is the last of this one. */
function endOf(month: string): string {
  const year = Number(month.slice(0, 4))
  const index = Number(month.slice(5, 7))
  return new Date(Date.UTC(year, index, 0)).toISOString().slice(0, 10)
}

/**
 * The nights a windowed list covers.
 *
 * A list reached from the tab bar browses everything from `on` forward. A list reached by
 * pressing "+4 more" on a Thursday is an answer about that Thursday, and paging it into
 * September is not what the press meant.
 */
function within(entry: CalendarEntry, on: string, only: 'day' | 'week' | 'month'): boolean {
  if (only === 'day') return entry.iso === on
  if (only === 'month') return entry.month === monthOf(on)
  const monday = mondayOf(on)
  return entry.iso >= monday && entry.iso <= shift(monday, 6)
}

function step(month: string, by: number): string {
  const [year, mon] = month.split('-').map(Number)
  const d = new Date(Date.UTC(year ?? 2026, (mon ?? 1) - 1 + by, 1))
  return d.toISOString().slice(0, 7)
}

function shift(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/**
 * Where a step of `by` from `on` lands: that many weeks on the week, otherwise the 1st of the
 * month that many months on. One expression for the arrows and for the window the next step
 * will need, so the prefetch asks for the window the step then finds already held.
 */
function stepped(grid: CalendarAddress['grid'], on: string, by: number): string {
  return grid === 'week' ? shift(on, by * 7) : `${step(monthOf(on), by)}-01`
}

/**
 * The calendar: one payload, one piece of state, and three ways of drawing it.
 *
 * This is the file the port existed to make possible. The calendar used to be a second
 * document — its own masthead, its own stylesheet, a full reload to reach it — and the
 * complaint that started the rewrite was that moving between it and the front page was
 * "clearly not the same object mutated". It is a view inside the shell now: the bar above it
 * is mounted once and never by a route, and switching to this is a canvas swap rather than
 * a page load.
 *
 * State lives in the address, because a view that cannot be sent is not much of a calendar.
 * `?view=picked&grid=week&on=2026-08-20` is the grammar the front page's continuations have
 * spoken since the routing rebuild, and `address.ts` is the only thing that reads or writes
 * it.
 */
export function Calendar() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  // The page a sheet opens over. Carried so the sheet does not replace the calendar.
  const here = useLocation()
  const rows = useCalendar()
  const [phone, setPhone] = useState(isPhone)
  const me = rows.me
  /**
   * Counted by the server, over the whole calendar.
   *
   * It used to be counted here, which was the same answer while the browser held every row
   * and is not now: a tab counting the window would say "Curated 41" in August and
   * "Curated 312" in December, which reads as the number being broken rather than the
   * window being small.
   *
   * Read at the top of the component, before `address`, because the address's fallback for
   * a tab hidden on a zero count needs it.
   */
  const counts = rows.counts

  /**
   * How many months past the anchored one the list has been scrolled into.
   *
   * The list is the one grid that runs on rather than paging, so it is the one that can
   * walk off the end of the loaded window. The two arrows-driven grids cannot: they draw
   * exactly one month or one week, and moving changes `on`.
   */
  const [reach, setReach] = useState(0)

  useEffect(() => {
    const watch = window.matchMedia?.('(max-width: 720px)')
    if (!watch) return
    const seen = () => setPhone(watch.matches)
    watch.addEventListener('change', seen)
    return () => watch.removeEventListener('change', seen)
  }, [])
  const address = useMemo(() => {
    const asked = readAddress(params.toString(), { me, phone })
    // `readAddress` has closed a tab that is not this reader's; what it cannot close is one
    // hidden on its count, because an address carries no counts. My venues is hidden on its
    // number, so a `?view=venues` link opened by a reader who follows nothing would draw a
    // view with no tab in the row to say which it is. The same `opens` is asked again here
    // with the counts the tab row is built from, rather than a second rule about it.
    //
    // `rows.loaded ? counts : undefined` is inert today, and was shown to be: `me` and
    // `counts` arrive in the same boot (`Data.tsx`: `me: boot?.me ?? null`, `loaded: boot !==
    // null`, and no `setBoot` writes `null`), so before it lands `me` is null and `opens`
    // answers from `MINE` without reading the counts — the same for `{}` as for `undefined`,
    // run for all six views. It stays so the landing does not come to depend on that
    // coupling: were `me` ever known before the boot, `{}` would read as a zero and throw a
    // reader off their own link before the number that keeps them there arrived.
    return opens(asked.view, me, rows.loaded ? counts : undefined)
      ? asked
      : { ...asked, view: defaultView(me) }
  }, [params, me, phone, counts, rows.loaded])

  /** One writer for the address, so no two pieces of state can disagree about it. */
  const go = useCallback(
    (change: Partial<CalendarAddress>) => {
      // No view guard here: `readAddress` has already closed a tab that is not this
      // reader's, and the UI offers no other.
      const next = { ...address, ...change }
      setParams(new URLSearchParams(writeAddress(next, { me, phone })), { replace: true })
    },
    [address, me, phone, setParams],
  )

  /**
   * How many gigs each tab holds. Counted off the same list the tabs filter, so a number and
   * the page it leads to can never disagree — and counted before the window narrows it,
   * because a tab says how much is in that view rather than how much survived the filter you
   * are currently inside.
   */
  /**
   * The far edge this view needs, and the whole of the windowing from the browser's side.
   *
   * Asking for everything cost 402ms and 733KB — 7,247 rows so a month grid could draw at
   * most 124 chips. A month costs 108ms and 154KB. `load` is monotonic, so stating the need
   * on every render is safe: paging back to August after reaching December asks for less
   * and changes nothing.
   *
   * Each grid is given a little more than it draws. The month grid's first and last rows
   * are the trailing days of the neighbouring months, and a week can straddle a boundary —
   * so a window cut exactly at the month would leave the corners of the grid empty.
   */
  // Back to the anchored month whenever the list stops being the same list.
  useEffect(() => {
    setReach(0)
  }, [address.view, address.grid, address.on, address.only])

  /**
   * The night the grids are anchored on. The address carries one only once the reader has
   * chosen; before that it is today, which is the server's answer rather than the browser's.
   */
  const on = address.on ?? rows.today
  const month = monthOf(on)
  const todayMonth = monthOf(rows.today)

  /**
   * The last month Everything's list may draw — the anchored month plus every step the
   * sentinel has taken — or null on every other grid and tab, where the list runs on
   * through `/entries`' window as it always has.
   *
   * `ListView` opens months in the order they appear in the rows it is given and asks for
   * more only once every month it holds is open. In a gone month `/entries` holds what this
   * viewer claimed and nothing else — some months, not others — so an unbounded list
   * anchored on last November could open February after December, skipping a January
   * nobody claimed anything in and whose history nothing had asked for; and a January
   * arriving later would land in the middle of its months and reset it to one. Bounding the
   * rows at the month the sentinel has reached makes the months the list holds the months
   * this route has asked for, so each step is one month, appended.
   *
   * The bound used to lift at today's month, when every month the list drew was `/entries`'
   * and there was nothing else to ask for. A month ahead is now asked for whole, from the
   * same store, the moment the list draws it — so the list has to say which months it draws
   * ahead as well as behind, and `reach` is that only while the bound holds: without it the
   * list opens next month from `/entries`' window on its own and `reach` moves one month
   * late, after the reader has scrolled through a night drawn at the cell's depth.
   */
  const listEdge = useMemo(() => {
    if (address.view !== 'all' || address.grid !== 'list' || address.only || !todayMonth) return null
    return step(month, reach)
  }, [address.view, address.grid, address.only, month, reach, todayMonth])

  /**
   * Whether the bounded list has a month left to step into: the calendar's last night is
   * beyond the edge, or today's month is — a list walking out of the past has to reach
   * today whatever lies ahead of it, and a calendar whose last listing has gone would
   * otherwise strand it. `rows.more` is `/entries`' own answer and is kept beside this,
   * because the edge sits a month behind the window `need` asks for.
   */
  const listMore = useMemo(() => {
    if (listEdge === null) return false
    const last = rows.last ? monthOf(rows.last) : todayMonth
    return listEdge < (last > todayMonth ? last : todayMonth)
  }, [listEdge, rows.last, todayMonth])

  const [need, ahead] = useMemo((): [string, string | null] => {
    const measure = (on: string): string => {
      // Nothing until there is a night to measure from. `on` falls back to `rows.today`, which
      // is `''` until the boot payload lands — and every window below is measured from it.
      //
      // `mondayOf('')` builds `new Date('T12:00:00Z')`, which is an Invalid Date, and
      // `toISOString` on one throws `RangeError`. Thrown from a `useMemo` above the
      // `!rows.loaded` guard, that took the whole route down: `/calendar?grid=week` loaded
      // cold — a reload, a bookmark, a shared link, a new tab — rendered an empty `#root` and
      // a white screen. Pressing WEEK was fine, because by then today was known.
      //
      // The month path survived the same empty string by accident rather than by design:
      // `monthOf('')` is `''.slice(0, 7)`, which is a harmless empty string, so only the week
      // grid ever reached the date arithmetic.
      // Today is not known until the boot payload lands, and `load` is what fetches it — so a
      // window cannot be computed yet and one still has to be asked for. Guarding the *call*
      // deadlocked the page: nothing was requested, so today never arrived, so nothing was
      // ever requested. The calendar sat on its spinner at every width and every grid.
      //
      // A bounded first ask, and it used to be `null` — `load`'s word for the whole calendar.
      //
      // The reasoning above is right that a window cannot be *measured* before today lands,
      // and wrong that the only alternative is everything. The browser's own clock is within a
      // day of the server's night, and a horizon does not need to be exact: it needs to be a
      // horizon. Asking for roughly six weeks fetches the part of the calendar a reader opens
      // on, and the real window replaces it the moment today arrives — `load` is monotonic, so
      // a wider request later can only widen.
      //
      // Measured against production, 8 September: unbounded is 8.5 MB raw, 1,010 KB gzipped
      // and 8.2s; a month-and-a-bit is 4.7 MB, 592 KB and 3.1s. The row count is what the
      // window buys — the near weeks are the dense ones — so this is the cheap half of the
      // problem and the payload's per-row weight is the other, filed separately.
      if (!on) {
        // The same window the list asks for the moment today lands — `endOf(step(month, 1))`
        // below — measured off the browser's clock instead of the server's night. Matching it
        // is the difference between one request and two: a narrower guess is replaced a
        // moment later by the real window, and the calendar fetches most of itself twice.
        return endOf(step(monthOf(new Date().toISOString().slice(0, 10)), 1))
      }
      if (address.grid === 'week') return shift(mondayOf(on), 13)
      const anchored = monthOf(on)
      if (address.grid === 'list') {
        // A list walking the past on Everything is fed month by month from the history store,
        // and `/entries` need hold no more for it than it holds for a list opened today: a
        // window starts at `since` whatever its far edge, and the gone rows before `since`
        // are the claimed body's, so the window a list opened today asks for is the one this
        // list needs. It stays put while the sentinel walks the past — a far edge measured
        // from a November anchor grew with every month opened, and each growth past what was
        // held fetched the same claimed rows again with one more month on the end. Once the
        // edge is today's month or past it, the window is one month beyond the edge, which is
        // what `reach + 1` below gives every other tab: the month the sentinel steps into
        // next is held before it is drawn.
        //
        // Off the browser's clock until the server's today lands, as the `!on` guess above
        // is, so the cold open of a past-anchored list is one request and not one replaced.
        const browserMonth = monthOf(new Date().toISOString())
        if (listEdge !== null) return endOf(step(listEdge < todayMonth ? todayMonth : listEdge, 1))
        if (!todayMonth && address.view === 'all' && anchored < browserMonth)
          return endOf(step(browserMonth, 1))
        return endOf(step(anchored, reach + 1))
      }
      return shift(endOf(anchored), 7)
    }
    /*
     * And the window the next forward step will need, measured by the same arithmetic from
     * where that step lands, so the step finds it held and draws at once. The month grids
     * only: the week's own window already runs to the end of the following week, and the
     * list's a month past the month it has reached, so each already holds its next step and a
     * prefetch would only be one more request.
     */
    const next = on && address.grid === 'month' ? measure(stepped(address.grid, on, 1)) : null
    return [measure(on), next]
  }, [address.grid, address.view, on, reach, listEdge, todayMonth])

  /*
   * One effect, and `load` before `prefetch` inside it: the order is load-bearing.
   *
   * The provider holds a prefetch back only behind the `load` edge it already knows, and
   * both calls send at once if nothing is out. A `prefetch` stated while the provider still
   * knows the previous render's edge, already landed, is sent to the prefetch's far edge,
   * and the window on screen arrives only inside that wider request: a week switched to
   * month draws its month only once the following month's nights have come too. Stating
   * `need` first makes it the edge the prefetch queues behind. As two effects this held
   * only while the `load` effect was declared above the other, since React runs a
   * component's effects in declaration order; one body makes it the order of the two lines
   * below. `load` is monotonic, so calling it again when only `ahead` has moved asks for
   * nothing.
   */
  useEffect(() => {
    rows.load(need)
    if (ahead) rows.prefetch(ahead)
  }, [rows, need, ahead])

  /**
   * The months this grid asks `/history` for — each held whole, gone nights and nights
   * ahead alike — so that a night Everything draws whole is drawn from every row on it.
   *
   * Only on Everything: the other five tabs show, on a gone night, the rows the reader
   * claimed, which `/entries` carries from `since` on and the claimed body before it
   * (`reachesBack` below), and on a night ahead the rows in their tab,
   * which `/entries` sends whatever the night's depth — My venues too, for the viewer it is
   * drawn for, because `_payload`'s `whole_tracked` passes every tracked row on a night
   * ahead. Behind the reader that tab holds what they claimed and nothing more, and
   * widening this gate would not change it: `/history` ranks for nobody, so every row it
   * returns carries `tracked` false by construction and the tab would page into a gone
   * month and draw nothing. The month key is `CalendarEntry.month` as the server writes it
   * — the night's month — so a week straddling two months asks for both and a 02:00 gig on
   * the 1st is asked for under the month it is drawn in.
   *
   * Which views ask for a month ahead is decided by what they draw of a night. The list —
   * windowed or not — and the week draw a night whole, or with a "+N" that opens it whole,
   * so they name every month they draw; `/entries` sends a night ahead at the depth the
   * month cell draws, and a list fed from that alone drew four rows and a "+8 more" that
   * opened the same four. The month grids draw `MAX_CHIPS` and a "+N" counted off
   * `nights`, which `/entries` already carries, so they name only months up to today's:
   * today's month is included because its earlier nights have gone, and because the day a
   * cell's "+N" opens is then already in hand.
   *
   * The list names the anchored month and every month the sentinel has stepped into after
   * it — `listEdge` — from the earlier of the anchor and today, `ListView`'s own `start`,
   * because that is where its first open month is. A windowed list (`only=`) names the
   * window's months.
   */
  const drawing = useMemo((): string[] => {
    if (address.view !== 'all' || !on || !todayMonth) return []
    if (address.grid === 'week' || (address.grid === 'list' && address.only === 'week')) {
      const monday = mondayOf(on)
      return [...new Set([monthOf(monday), monthOf(shift(monday, 6))])]
    }
    if (address.grid === 'list') {
      if (address.only) return [month]
      const first = month < todayMonth ? month : todayMonth
      const last = step(month, reach)
      const months: string[] = []
      for (let m = first; m <= last; m = step(m, 1)) months.push(m)
      return months
    }
    return month <= todayMonth ? [month] : []
  }, [address.view, address.grid, address.only, on, month, todayMonth, reach])

  const { wantGone } = rows
  useEffect(() => {
    for (const m of drawing) wantGone(m)
  }, [wantGone, drawing])

  /**
   * The first night this view draws: the Monday heading a month grid, the Monday of a week,
   * the night, week or month a windowed list is confined to, and otherwise the 1st of the
   * month a list opens on — `ListView`'s `start`, the earlier of the anchor and today.
   * Null until today is known.
   */
  const earliest = useMemo((): string | null => {
    if (!on || !todayMonth) return null
    if (address.grid === 'week') return mondayOf(on)
    if (address.grid === 'month') return mondayOf(`${month}-01`)
    if (address.only && address.on) {
      if (address.only === 'day') return address.on
      if (address.only === 'week') return mondayOf(address.on)
      return `${month}-01`
    }
    return `${month < todayMonth ? month : todayMonth}-01`
  }, [address.grid, address.only, address.on, on, month, todayMonth])

  /**
   * Whether this view draws a night before `/entries`' edge, and so needs the claimed body
   * for its gone rows there. Compared with the server's `since` rather than with any rule
   * about where the edge falls, so the two cannot disagree; a boot naming no edge holds
   * every gone row it was sent and reaches back for nothing.
   */
  const reachesBack = !!rows.since && !!earliest && earliest < rows.since

  const { wantClaimed } = rows
  // On the view as well as the night: `wantClaimed` is idempotent once it has landed, so
  // re-running this costs nothing, and after a refusal it is the next view — a tab, a grid,
  // a step — that asks again rather than a render loop against a server that said no.
  useEffect(() => {
    if (reachesBack) wantClaimed()
  }, [wantClaimed, reachesBack, earliest, address.view, address.grid])

  /*
   * What this view draws from that has not arrived: the months of `drawing` the history store
   * has not landed, and the claimed body when the view reaches back for it and it is not here.
   * The list's `extending` and the grids' pending nights are both read off these two, so the
   * list and the grids cannot disagree about whether a store is still out.
   */
  const monthsOut = useMemo(() => drawing.filter(m => !(m in rows.gone)), [drawing, rows.gone])
  const claimedOut = reachesBack && rows.claimed === null

  /**
   * Whether a night the grid draws is still waiting for rows: past `/entries`' held edge, in a
   * month the history store has not landed, or before `since` while the claimed body is out.
   * Asked of the provider's edge and the stores, never of whether the night has rows — a held
   * night with nothing on it is not waiting, and must not look as though it were.
   */
  const { holds, since } = rows
  const pending = useCallback(
    (night: string) =>
      !holds(night) ||
      monthsOut.includes(monthOf(night)) ||
      (claimedOut && !!since && night < since),
    [holds, monthsOut, claimedOut, since],
  )

  /**
   * A step of the month grid, timed until the month it landed on is drawn from rows that have
   * arrived: at once when they were held, or when the widening or the month's history lands.
   * Asked of `pending`, the test the grid draws its waiting nights by, so the two agree.
   */
  const stepping = useRef<Settle | null>(null)
  useEffect(() => {
    if (stepping.current && !pending(`${month}-01`) && !pending(endOf(month))) {
      stepping.current(true)
      stepping.current = null
    }
  })

  /**
   * The next few nights with something on, for the toolbar's chips.
   *
   * From the loaded rows, so a night that would open on nothing is never offered — which is
   * the whole difference between this and a date picker, and why it is worth the width it
   * takes. Today first whether or not it has anything, because a reader who has paged away
   * needs the way back more than they need a fourth Friday.
   */
  const nextNights = useMemo(() => {
    const nights = new Set<string>()
    for (const e of rows.entries) if (e.iso >= rows.today) nights.add(e.iso)
    return [rows.today, ...[...nights].sort().filter(n => n !== rows.today)].slice(0, 6)
  }, [rows.entries, rows.today])

  const shown = useMemo(() => {
    if (!rows.loaded) return []
    let pool = rows.entries
    // The months held whole under `/entries`' window. `/entries` wins a row both hold: it
    // carries this viewer's marks and the month's row carries nobody's, so the other way
    // round a tick the reader pressed would draw unlit beside its own chip — on a gone
    // night, and now on a night ahead too, where the claimed row is in both bodies.
    const held = new Set(rows.entries.map(e => e.id))
    // Then the claimed body: this viewer's rows on the nights before `since`, which
    // `/entries` no longer sends, with their marks. On every tab, and ahead of the month
    // store for the reason `/entries` is — where a row is in both, this copy is the one
    // with the viewer's tick on it. `inView` below admits each to its tab, as it admits
    // `/entries`' own rows. Nothing here is on or after `since`, so on a view that does not
    // reach back these rows sit outside every night it draws.
    const claimed = (rows.claimed ?? []).filter(e => !held.has(e.id))
    for (const e of claimed) held.add(e.id)
    // On Everything, every row of the months it is drawing. On the other four tabs, the
    // month store still has rows to give: a mark pressed on Everything lands on the copy
    // the month store holds — `decide` patches both stores, and on a night ahead most of
    // Everything's rows are held there alone — and `/entries` does not carry that row as
    // claimed until its next full load. So a row the reader saved from Everything's list
    // was on no tab until a reload. A month-store row joins a tab's pool by the tab's own
    // rule, `inView`, which is the one definition of what a personal mark puts where; the
    // month bodies carry no `friends`, so that tab takes nothing from here, as it should.
    // Keyed on the tab and not on whether `drawing` is empty: Everything's month grid on a
    // month ahead names no month and must not draw every month held into its corners.
    const everything = address.view === 'all'
    const extra = claimed.concat(
      (everything ? drawing : Object.keys(rows.gone)).flatMap(m =>
        (rows.gone[m] ?? []).filter(
          e => !held.has(e.id) && (everything || inView(e, address.view, me)),
        ),
      ),
    )
    // By night, and stably: the list groups its days in the order it first meets them, so
    // month rows appended after `/entries`' put a night nobody claimed anything on below
    // every night somebody did. Within a night the order stands — the viewer's own rows,
    // as the server ranked them, then the public ranking of the rest.
    if (extra.length)
      pool = [...rows.entries, ...extra].sort((a, b) =>
        a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0,
      )
    if (listEdge) pool = pool.filter(e => e.month <= listEdge)
    const inTab = pool.filter(e => inView(e, address.view, me))
    if (address.grid !== 'list' || !address.only || !address.on) return inTab
    return inTab.filter(e => within(e, address.on as string, address.only as 'day'))
  }, [
    rows.loaded,
    rows.entries,
    rows.claimed,
    rows.gone,
    drawing,
    listEdge,
    address.view,
    address.grid,
    address.only,
    address.on,
    me,
  ])

  /**
   * The night the grids are anchored on. The address carries one only once the reader has
   * chosen; before that it is today, which is the server's answer rather than the browser's.
   */

  if (rows.failed)
    return <p className={s.trouble}>The calendar could not be reached. {rows.failed}</p>
  if (!rows.loaded) return <main className={s.wrap} aria-busy="true" />

  // Raw ISO before this — `2026-08-23 – 2026-08-29` — where every other date on the site is
  // written for a reader, and naming a week the grid was not drawing. `weekLabel` finds the
  // Monday itself; see the note there for why that is not the caller's job. One string for
  // every width: on the phone the toolbar gives the heading a line of its own rather than
  // asking for a shorter name — with one exception. A week that spans two months carries
  // two full month names, and those overrun every phone width even on a line of their own,
  // so that shape alone takes the three-letter months on the phone. `weekLabel` reads only
  // `months[i]`, so the short array is a valid input and its cross-month branch still fires.
  const weekMonths = phone && weekSpansMonths(on) ? MONTHS.map(m => m.slice(0, 3)) : MONTHS
  const label =
    address.grid === 'week'
      ? weekLabel(on, weekMonths)
      : `${MONTHS[Number(month.slice(5)) - 1]} ${month.slice(0, 4)}`

  // The calendar's own range, not the window's — from the server for the same reason the
  // counts are.
  const first = rows.first ?? rows.today
  const last = rows.last ?? rows.today

  return (
    <MarksProvider value={{ me, save: rows.save, going: rows.going, star: rows.star }}>
      <main className={s.wrap}>
      <Toolbar
        view={address.view}
        grid={address.grid}
        density={address.density}
        me={me}
        monthLabel={label}
        canPrev={on > first}
        canNext={on < last}
        counts={counts}
        onView={view => go({ view })}
        /* WEEK means *this* week. Coming from a month you had stepped to in November it
           otherwise landed on the week around whatever `on` happened to be — a week nobody
           asked for, under a button naming the one week everybody knows. */
        onGrid={grid => go(grid === 'week' ? { grid, on: rows.today } : { grid })}
        /* In the address, not in component state. It was the one calendar control that
           was not, so it was lost on reload and could not be sent to anybody. */
        onDensity={density => go({ density })}
        onStep={by => {
          if (address.grid === 'month') stepping.current = act('month')
          go({ on: stepped(address.grid, on, by) })
        }}
        // `rows.today` is the server's night, not the browser's: the calendar is cut at 05:00
        // London and a phone in another timezone asking `new Date()` would land on the wrong
        // one — the same reason `on` falls back to it rather than to a local date.
        onToday={() => go({ on: rows.today })}
        atToday={on === rows.today}
        // The next nights that have something on, from the rows already loaded. Off `shown`
        // rather than off every entry, so the chips answer the tab the reader is in: a night
        // whose only gig is one you have not saved is not a night your Saved tab can offer.
        /*
         * The phone's list, and nowhere else.
         *
         * Two reasons, and they are different. On a laptop the row is chrome nobody asked
         * for: there is width for a month grid and arrows, and a reader with a pointer pages
         * rather than thumbs. And in the *month* grid — at any width — the cells already are
         * the day selector, so a second one above them is two controls for one question, and
         * the chips cannot even scroll a grid to a day the way they scroll a list to one.
         */
        days={phone && address.grid === 'list' ? nextNights : []}
        on={on}
        onDay={night => {
          go({ on: night })
          /*
           * And on the list, actually go there.
           *
           * A list is one continuous timeline, so a night is a place in it rather than a
           * filter over it — the chip wrote `on=` and the list, which does not filter by
           * day, did not move a pixel. The grids read `on` as the window they draw; the
           * list reads it as the month it opens from, and the scroll is what makes the
           * press mean anything.
           *
           * After paint, because the month the night belongs to may not be rendered until
           * the anchor above has changed it.
           */
          if (address.grid !== 'list') return
          requestAnimationFrame(() => {
            document
              .querySelector(`[data-date="${night}"]`)
              ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
          })
        }}
      />

      {/*
        * Two months, and which one you get is the width. `MonthGrid`'s docstring has said so
        * since the port — "the phone renders the same month as a grid of dots, which is a
        * different component; this one is only ever drawn above 720px" — and neither half
        * was true: the dots component was never carried over from the Python, and nothing
        * gated this by width. A phone got the desktop grid, 51px cells and all.
        */}
      {address.grid === 'month' && phone ? (
        <PhoneMonth
          entries={shown}
          /* Same key, same condition as the desktop grid below — see the note there. */
          nights={address.view === 'all' ? rows.nights : undefined}
          today={rows.today}
          month={month}
          me={me}
          selected={on}
          pending={pending}
          onSelect={night => {
            // A day in the greyed tail belongs to a neighbouring month, so tapping it pages
            // there rather than selecting a day this grid is about to stop showing.
            const its = night.slice(0, 7)
            go(its === month ? { on: night } : { on: night, grid: 'month' })
          }}
        />
      ) : null}

      {address.grid === 'month' && !phone ? (
        <MonthGrid
          entries={shown}
          /*
           * Only on Everything, because that is the only tab drawing the rows these numbers
           * count. The others filter the payload, and the cap sends them whole — every
           * claimed row survives it, and so does every picked and every tracked one — so on
           * those tabs the rows in hand are the night and the payload's total is about a
           * different list.
           */
          nights={address.view === 'all' ? rows.nights : undefined}
          today={rows.today}
          month={month}
          me={me}
          onSave={rows.save}
          onGoing={rows.going}
          onDay={iso => go({ grid: 'list', on: iso, only: 'day' })}
          pending={pending}
        />
      ) : null}

      {address.grid === 'week' ? (
        <WeekView
          entries={shown}
          /* Same key, same condition as the two month grids — see the note there. A week
             column has more room than the cap sends, so without this the count it draws is
             not merely low, it is absent on every night. */
          nights={address.view === 'all' ? rows.nights : undefined}
          today={rows.today}
          weekStart={mondayOf(on)}
          pending={pending}
          onDay={iso => go({ grid: 'list', on: iso, only: 'day' })}
          onShift={days => go({ on: shift(on, days) })}
        />
      ) : null}

      {address.grid === 'list' ? (
        <ListView
          entries={shown}
          today={rows.today}
          density={phone ? 'compact' : address.density}
          me={me}
          // The row is looked up in what the list was handed, so a click on the row itself
          // seeds the sheet exactly as its name link does — `ListView` reports only the id.
          onOpen={id =>
            navigate(`/e/${id}`, { state: opening(here, shown.find(e => e.id === id)) })
          }
          onSave={rows.save}
          onGoing={rows.going}
          // Paging is for browsing. A window is an answer, and an answer that keeps going
          // is not one.
          paging={!address.only}
          // The list can scroll past what is loaded, so it is told there is more and given
          // the way to ask for it. Without this the window would read as the end of the
          // calendar and the scroll would simply stop.
          //
          // On Everything, "more" is the next month, which `reach` names and the history
          // store answers whole — in the past never a wider `/entries` window, which would
          // be asking for a far edge already held and waiting for nothing; ahead, both at
          // once, the window one month past the edge. The sentinel holds while a month it
          // is drawing has not arrived, as it holds while `/entries`
          // widens. "Not arrived" rather than "in flight", and it matters: the month is
          // asked for from an effect, after the render that named it, and a sentinel armed
          // in that render with nothing in flight fired again before the ask — two months
          // at once, the later landing first and the earlier resetting the list when it
          // came. Named-but-absent is true in the same render as `reach` moves.
          canExtend={rows.more || listMore}
          extending={rows.widening || (listEdge !== null && monthsOut.length > 0) || claimedOut}
          onExtend={() => setReach(n => n + 1)}
          window={
            address.only && address.on
              ? { only: address.only, on: address.on, clear: () => go({ only: null }) }
              : null
          }
          // What the month arrows move. A window is already the whole of what it is about,
          // so it anchors nothing — the arrows are disabled there anyway.
          anchor={address.only ? null : on}
        />
      ) : null}

      {/*
        * Two slots, and there were three.
        *
        * The third focused the search box in the masthead — a bar at the foot of the screen
        * pointing at a control at the top of it, so a reader saw "Search" twice on one page
        * and the lower one moved the cursor somewhere they were not looking. The masthead's
        * box is on every page of the site; this bar is the calendar's own, and what belongs
        * on it is the two ways of reading a calendar.
        */}
      <PhoneBar
        grid={address.grid}
        mode="calendar"
        /* WEEK means *this* week. Coming from a month you had stepped to in November it
           otherwise landed on the week around whatever `on` happened to be — a week nobody
           asked for, under a button naming the one week everybody knows. */
        onGrid={grid => go(grid === 'week' ? { grid, on: rows.today } : { grid })}
      />

      </main>
    </MarksProvider>
  )
}
